import React, { useEffect, useState } from "react";
import {
  getTrackingEvent,
  postTagEmailOffer,
} from "../../../../services/offers/offers.service";
import {
  CampaignType,
  ICONS_DEFAULT_VALUES,
  LayoutOptions,
  Offer,
  OffersIdSlots,
  OtherSettingsInterface,
  PlacementStage,
  QuerySaveEmail,
  RedeemBy,
  Template,
  TemplateIcons,
  TemplateOptionSettings,
} from "../../../../types/global.types";
import offerImageSrc from "./../../../../assets/images/offer-listening.png";
import TemplateIcon from "../../components/Icons/info.component";
import BlockedTag from "../../components/BlockedTag/blockedTag.component";

interface Props {
  offers: Offer[];
  eventOfferIds: OffersIdSlots[];
  settings: TemplateOptionSettings;
  settingsTemplate: Template;
  displayOnly: boolean;
  retailerId: string;
  campaignId: string;
  placementId: string;
  placementName: string;
  campaign: CampaignType;
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number,
    isShare?: boolean
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  otherSettings: OtherSettingsInterface | undefined;
  preEmail?: string;
  setPreEmail?: (email: string) => void;
  layoutId: string;
  layoutOptions: LayoutOptions | undefined;
}

const OfferItem = ({
  offers,
  eventOfferIds,
  settings,
  campaign,
  settingsTemplate,
  displayOnly,
  retailerId,
  campaignId,
  placementId,
  placementName,
  openSingle,
  onAdd,
  otherSettings,
  preEmail,
  setPreEmail,
  layoutId,
  layoutOptions,
}: Props) => {
  const [platform, setPlatform] = useState("");
  const [email, setEmail] = useState<string | undefined>();
  const [validEmail, setValidEmail] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [sentEmailIds, setSentEmailIds] = useState<string[]>([]);

  const getOfferSlotById = (
    offerId: string,
    offerIdAndSlots: OffersIdSlots[]
  ): number => offerIdAndSlots.find((f) => f.id === offerId)?.slot || 0;

  const handleAdd = (e: React.ChangeEvent<HTMLInputElement>, offer: Offer) => {
    const slot = getOfferSlotById(offer.id, eventOfferIds);
    const value = e.target.checked;
    if (displayOnly && !offer.tagBlocked.isBlocked) {
      onAdd(offer, value, slot);
      value && handleOpenEmailInput(offer);
    }
  };

  const handleOpenEmailInput = (offer: Offer) => {
    if (
      campaign.campaignGeneralOptions?.showAddToOrder &&
      campaign.campaignGeneralOptions?.emailCaptureOnOffers &&
      campaign.campaignGeneralOptions?.emailCaptureOnAddToOrder
    ) {
      const slot = getOfferSlotById(offer.id, eventOfferIds) || 0;
      openSingle(offer, false, true, slot);
    }
  };

  const handleGetGift = (
    event: React.MouseEvent<HTMLElement>,
    url: string,
    slot: number,
    offer: Offer
  ) => {
    if (
      event.target instanceof HTMLElement &&
      !event.target.classList.contains("new-look-item-info-icon") &&
      !event.target.classList.contains("new-look-item-share-icon") &&
      !event.target.classList.contains("new-look-item-email-icon") &&
      !event.target.classList.contains("email-me-now") &&
      !displayOnly &&
      offer.offerSettings.redeemBy === RedeemBy.webemail
    ) {
      window.open(
        url + `&tid=${settingsTemplate.identifier}&slot=${slot}`,
        "_blank"
      );
    }
  };

  const handleClickInfo = (
    event: React.MouseEvent | React.KeyboardEvent,
    offer: Offer
  ) => {
    event.preventDefault();
    const slot = getOfferSlotById(offer.id, eventOfferIds);
    openSingle(offer, false, false, slot);
    getTrackingEvent({
      event: "onreadmoreclick",
      offerId: offer.id,
      retailerId,
      campaignId,
      placementId,
      placementName,
      tid: settingsTemplate.identifier,
      slot,
      pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
      pltid: layoutId,
    });
  };

  const handleClickShare = (
    event: React.MouseEvent | React.KeyboardEvent,
    offer: Offer
  ) => {
    event.preventDefault();
    const slot = getOfferSlotById(offer.id, eventOfferIds);
    openSingle(offer, false, false, slot, true);
  };

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    // Check if the platform is iOS
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform("ios");
    }
    // Check if the platform is macOS
    else if (/macintosh|macintel|macppc|mac68k|macos/.test(userAgent)) {
      setPlatform("mac");
    } else {
      setPlatform("other");
    }
  }, []);

  const submitSaveForm = async (offer: Offer) => {
    if ((email && validEmail) || preEmail) {
      setLoading(true);
      const emailParams: QuerySaveEmail = {
        offerid: offer.id,
        email: email || preEmail || "",
        offerLink: offer.trackingUrl,
        companyId: retailerId,
        campaignId: campaignId,
        placementId,
        templateId: settingsTemplate.identifier,
        slot: getOfferSlotById(offer.id, eventOfferIds),
        orderId: offer.orderId,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        delayMinutes: 10,
        layoutId,
      };

      const response = await postTagEmailOffer(emailParams);
      getTrackingEvent({
        event: "onsendemailclick",
        retailerId,
        campaignId: campaignId,
        offerId: offer.id,
        placementId,
        placementName,
        tid: settingsTemplate.identifier,
        slot: getOfferSlotById(offer.id, eventOfferIds),
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        pltid: layoutId,
      });

      if (response?.data?.status) {
        setPreEmail && setPreEmail(email || preEmail || "");
        setSentEmailIds((prevState) => [...prevState, offer.id]);
      }
    }

    setLoading(false);
  };

  return (
    <>
      <div
        className={`swiper-item ${
          displayOnly && campaign.campaignGeneralOptions?.showAddToOrder
            ? "display"
            : "getbutton"
        }`}
      >
        {offers.map((offer: Offer) => {
          return (
            <div
              key={`new-look-${offer.id}`}
              // onClick={(e) =>
              //   handleGetGift(
              //     e,
              //     offer.trackingUrl,
              //     getOfferSlotById(offer.id, eventOfferIds),
              //     offer
              //   )
              // }
              className={`swiper-new-look-item ${
                displayOnly && campaign.campaignGeneralOptions?.showAddToOrder
                  ? "display"
                  : "getbutton"
              }`}
            >
              <input
                className="input"
                id={offer.id}
                type="checkbox"
                disabled={
                  !displayOnly ||
                  !campaign.campaignGeneralOptions?.showAddToOrder
                }
                checked={!displayOnly ? false : offer.isAdded || false}
                onChange={(e) => handleAdd(e, offer)}
              />
              {(layoutOptions?.showBadge ?? true) && (
                <span
                  style={{
                    position: "absolute",
                    top: "-8.5px",
                    left: "10px",
                    fontSize: "10px",
                    textTransform: "uppercase",
                    color: offer.badge?.fontColor,
                    backgroundColor: offer.badge?.backgroundColor,
                    padding: "0 6px",
                    borderRadius: "3px",
                    fontWeight: "600",
                    zIndex: 1,
                  }}
                  className="badge-settings"
                >
                  {offer.badge?.name}
                </span>
              )}
              <label
                htmlFor={offer.id}
                className="new-look-item-layout"
                style={{
                  backgroundColor: settings?.dealLayout?.backgroundColor,
                  fontSize: settings?.dealLayout?.fontSize,
                  color: settings?.dealLayout?.textColor,
                  border: settings.dealLayout?.borderColor
                    ? `1px solid ${
                        !displayOnly
                          ? settings.dealLayout?.borderColor
                          : offer.isAdded
                          ? settings.mainLayout?.highlightColor
                          : settings.dealLayout?.borderColor
                      }`
                    : `1px solid ${
                        !displayOnly
                          ? offer.badge?.useBorder
                            ? offer.badge?.backgroundColor
                            : "#ddd"
                          : offer.isAdded
                          ? settings.mainLayout?.highlightColor
                          : (offer.badge?.useBorder &&
                              offer.badge?.backgroundColor) ||
                            "#ddd"
                      }`,
                  position: "relative",
                }}
              >
                {offer.tagBlocked.isBlocked && (
                  <BlockedTag tagBlocked={offer.tagBlocked} />
                )}

                <div
                  className="new-look-border"
                  style={{ borderColor: settings?.mainLayout?.highlightColor }}
                ></div>

                <div className="new-look-item-image">
                  <img
                    src={offer.imageUrl ? offer.imageUrl : offerImageSrc}
                    alt={offer.title}
                  />
                </div>

                <div className={`new-look-item-content`}>
                  <div className="new-look-item-title">
                    <span
                      className={`new-look-item-title-text max-2lines `}
                      style={{
                        color: settings?.dealLayout?.textColor,
                        fontSize: `${settings?.dealLayout?.fontSize}px`,
                      }}
                    >
                      {offer.title}
                    </span>
                  </div>

                  <div
                    className="new-look-item-description"
                    style={{
                      color: settings?.dealLayout.textColor,
                      fontSize: `${
                        Number(settings?.dealLayout.fontSize) - 2
                      }px`,
                    }}
                  >
                    <span className="max-2lines">{offer.description}</span>
                  </div>

                  <div className="new-look-item-get">
                    {!displayOnly && (
                      <>
                        {!sentEmailIds.includes(offer.id) ? (
                          offer.offerSettings.redeemBy ===
                            RedeemBy.webemail && (
                            <div
                              className="new-look-item-action-get email-me-now"
                              style={{
                                color: settings?.dealLayout.buttonTextColor,
                                fontSize: `${settings?.dealLayout.buttonFontSize}px`,
                                fontWeight:
                                  settings?.dealLayout.buttonFontWeight,
                                backgroundColor:
                                  settings?.dealLayout.buttonBackgroundColor,
                                borderRadius:
                                  settings?.dealLayout.buttonRounding,
                                border: `1px solid ${
                                  settings?.dealLayout.buttonBorderColor ||
                                  settings?.dealLayout.buttonBackgroundColor
                                }`,
                                textTransform:
                                  settings?.dealLayout.buttonTextTransform,
                              }}
                              onClick={() => submitSaveForm(offer)}
                            >
                              {/* {offer?.offerCTAText || "Get offer now"} */}
                              Email me now
                            </div>
                          )
                        ) : (
                          <div className="item-action-get email-me-now">
                            <span className="label-success">
                              Thank you. You will receive an email in 10 minutes
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    <div className="new-look-item-icons">
                      <div
                        className="new-look-info"
                        onClick={(e) => handleClickInfo(e, offer)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleClickInfo(e, offer);
                          }
                        }}
                      >
                        {!settings?.dealLayout?.icons ||
                        settings?.dealLayout?.icons ===
                          TemplateIcons.default ? (
                          <TemplateIcon
                            icon={
                              settings?.dealLayout.iconInfo?.value ||
                              ICONS_DEFAULT_VALUES.iconInfo
                            }
                            color={settings?.dealLayout.textColor}
                          />
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M12 0C5.37 0 0 5.37 0 12C0 18.63 5.37 24 12 24C18.63 24 24 18.63 24 12C24 5.37 18.63 0 12 0ZM13.5 18.5C13.5 19.33 12.83 20 12 20C11.17 20 10.5 19.33 10.5 18.5V10.5C10.5 9.67 11.17 9 12 9C12.83 9 13.5 9.67 13.5 10.5V18.5ZM12 7C11.17 7 10.5 6.33 10.5 5.5C10.5 4.67 11.17 4 12 4C12.83 4 13.5 4.67 13.5 5.5C13.5 6.33 12.83 7 12 7Z"
                              fill={settings?.dealLayout.textColor}
                            />
                          </svg>
                        )}
                      </div>

                      {!displayOnly &&
                        campaign.sharingOptions?.allowSharing &&
                        campaign.sharingOptions.sharingPlatforms?.platforms
                          ?.length > 0 &&
                        offer?.offerSettings?.allowShare && (
                          <div
                            className="new-look-share"
                            onClick={(e) => handleClickShare(e, offer)}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleClickShare(e, offer);
                              }
                            }}
                          >
                            {platform === "ios" || platform === "mac" ? (
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12 3L8 6.85714M12 3L16 6.85714M12 3V16"
                                  stroke={settings?.dealLayout.textColor}
                                  strokeWidth="1.2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M14.5 10H16C17.6569 10 19 11.3431 19 13V19C19 20.6569 17.6569 22 16 22H8C6.34315 22 5 20.6569 5 19V13C5 11.3431 6.34315 10 8 10H9.5"
                                  stroke={settings?.dealLayout.textColor}
                                  strokeWidth="1.2"
                                />
                              </svg>
                            ) : (
                              <TemplateIcon
                                icon={
                                  settings?.dealLayout.iconShare?.value ||
                                  ICONS_DEFAULT_VALUES.iconShare
                                }
                                color={settings?.dealLayout.textColor}
                              />
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                {displayOnly && (
                  <div className="new-look-item-switch">
                    {campaign.campaignGeneralOptions?.showAddToOrder &&
                      (otherSettings?.showCheckboxSelect ? (
                        <div className="new-look-item-switch">
                          <label htmlFor={offer.id}>
                            <div className="new-look-select">
                              <div
                                className={`new-look-checkbox ${
                                  offer.isAdded ? "checked" : ""
                                }`}
                                style={{
                                  backgroundColor: offer.isAdded
                                    ? settings?.mainLayout.highlightColor
                                    : "transparent",
                                  borderColor: offer.isAdded
                                    ? settings?.mainLayout?.highlightColor
                                    : settings.dealLayout?.borderColor ||
                                      "#ddd",
                                }}
                              >
                                {offer.isAdded && (
                                  <svg
                                    width="12"
                                    height="9"
                                    viewBox="0 0 12 9"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M11 1L4 8L1 5"
                                      stroke="white"
                                      strokeWidth="1.2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                      ) : (
                        <div className="new-look-switch-wrapper">
                          <label htmlFor={offer.id}>
                            <span
                              className={
                                offer.isAdded
                                  ? "new-look-switch-toggle new-look-switch-toggle--selected"
                                  : "new-look-switch-toggle"
                              }
                              style={{
                                backgroundColor: offer.isAdded
                                  ? settings?.mainLayout?.highlightColor
                                  : "#f1f2f3",
                              }}
                            >
                              <span
                                className={
                                  offer.isAdded
                                    ? "new-look-switch-toggle-before new-look-switch-toggle-before--selected"
                                    : "new-look-switch-toggle-before"
                                }
                              ></span>
                            </span>
                          </label>
                        </div>
                      ))}
                  </div>
                )}
              </label>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default OfferItem;
